import { useUploadFile } from '@/api'
import { UPLOAD_LIMITS } from '../types/upload-types'
import type { UploadStatus } from '../types/upload-types'

export type ProgressCallback = (fileKey: string, status: UploadStatus) => void

export interface BulkUploadParams {
  files: File[]
  partnerOrganizationId?: string
  documentType: string
  userOrgType: string
  isHeadless?: boolean
  onProgress?: ProgressCallback
}

export interface BulkUploadResult {
  fileName: string
  success: boolean
  error?: string
}

/**
 * Hook for uploading multiple files with progress tracking and concurrency control.
 *
 * Handles:
 * - Batch uploads with configurable concurrency
 * - Progress tracking for each file
 * - Error handling
 *
 * @example
 * ```tsx
 * const { uploadFiles } = useBulkFileUpload()
 *
 * const results = await uploadFiles({
 *   files: selectedFiles,
 *   partnerOrganizationId: 'abc-123',
 *   documentType: 'SPA',
 *   userOrgType: 'Manufacturer',
 *   onProgress: (fileName, status) => {
 *     console.log(`${fileName}: ${status.status} - ${status.progress}%`)
 *   }
 * })
 * ```
 */
/**
 * Groups files into upload batches, in order. A batch holds up to
 * CONCURRENT_UPLOADS files and at most MAX_BATCH_SIZE bytes, because the API
 * keeps each upload in memory. A file at the size limit uploads on its own.
 */
function toBatches(files: File[]): { start: number; batch: File[] }[] {
  const batches: { start: number; batch: File[] }[] = []
  let current: File[] = []
  let currentSize = 0

  files.forEach((file, index) => {
    if (
      current.length > 0 &&
      (current.length >= UPLOAD_LIMITS.CONCURRENT_UPLOADS ||
        currentSize + file.size > UPLOAD_LIMITS.MAX_BATCH_SIZE)
    ) {
      batches.push({ start: index - current.length, batch: current })
      current = []
      currentSize = 0
    }
    current.push(file)
    currentSize += file.size
  })

  if (current.length > 0) {
    batches.push({ start: files.length - current.length, batch: current })
  }

  return batches
}

export function useBulkFileUpload() {
  const { mutateAsync: uploadFile } = useUploadFile()

  const uploadFiles = async ({
    files,
    partnerOrganizationId,
    documentType,
    userOrgType,
    isHeadless,
    onProgress
  }: BulkUploadParams): Promise<PromiseSettledResult<BulkUploadResult>[]> => {
    if (!documentType || !userOrgType) {
      throw new Error('Missing required parameters')
    }

    const results: PromiseSettledResult<BulkUploadResult>[] = []

    // Process files in batches for concurrency control
    for (const { start: i, batch } of toBatches(files)) {
      const batchPromises = batch.map(async (file, batchIndex) => {
        const fileIndex = i + batchIndex
        const fileKey = `${fileIndex}-${file.name}`

        // Update progress: pending → uploading
        onProgress?.(fileKey, {
          fileName: file.name,
          status: 'uploading',
          progress: 0
        })

        let result
        try {
          // Upload using React Query mutation
          result = await uploadFile({
            file,
            partnerOrganizationId,
            documentType,
            userOrgType,
            isHeadless,
            onProgress: (percent) => {
              onProgress?.(fileKey, {
                fileName: file.name,
                status: 'uploading',
                progress: percent
              })
            }
          })
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Upload failed'

          onProgress?.(fileKey, {
            fileName: file.name,
            status: 'error',
            progress: 0,
            error: errorMessage
          })

          const uploadError = new Error(errorMessage)
          ;(uploadError as any).fileName = file.name
          ;(uploadError as any).success = false
          throw uploadError
        }

        if (!result.success) {
          const errorMessage = result.error || 'Upload failed'
          onProgress?.(fileKey, {
            fileName: file.name,
            status: 'error',
            progress: 0,
            error: errorMessage
          })

          const uploadError = new Error(errorMessage)
          ;(uploadError as any).fileName = file.name
          ;(uploadError as any).success = false
          throw uploadError
        }

        // Success!
        onProgress?.(fileKey, {
          fileName: file.name,
          status: 'success',
          progress: 100
        })

        return {
          fileName: file.name,
          success: true
        }
      })

      const batchResults = await Promise.allSettled(batchPromises)
      results.push(...batchResults)
    }

    return results
  }

  return {
    uploadFiles
  }
}
